import React, { useState, useEffect } from 'react';
import { Client, Project } from '../types';
import { getClients, createClient, updateClient, deleteClient } from '../api/client';

const COLOR_PRESETS = [
  '#2563EB', '#7C3AED', '#DC2626', '#16A34A', '#D97706',
  '#0891B2', '#DB2777', '#059669', '#EA580C', '#6366F1',
];

function ClientForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: Partial<Client>;
  onSave: (data: { name: string; color: string; notes: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name || '');
  const [color, setColor] = useState(initial?.color || '#2563EB');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ name, color, notes });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>Client Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Acme Corp"
          required
          autoFocus
        />
      </div>
      <div>
        <label>Brand Color</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
          {COLOR_PRESETS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: c,
                border: color === c ? '3px solid #0F172A' : '2px solid transparent',
                cursor: 'pointer',
                outline: color === c ? '2px solid white' : 'none',
                outlineOffset: '-3px',
              }}
            />
          ))}
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            style={{ width: 28, height: 28, padding: 0, border: 'none', borderRadius: '50%', cursor: 'pointer' }}
          />
        </div>
      </div>
      <div>
        <label>Notes &amp; Background</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Client background, industry, key contacts, goals, preferences..."
          rows={4}
          style={{ resize: 'vertical' }}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <button type="button" onClick={onCancel} className="btn btn-secondary btn-sm">Cancel</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
          {saving ? 'Saving...' : initial?.id ? 'Update Client' : 'Create Client'}
        </button>
      </div>
    </form>
  );
}

export default function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    getClients()
      .then((data) => setClients([...data].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (data: { name: string; color: string; notes: string }) => {
    const client = await createClient(data);
    setClients((prev) =>
      [...prev, client].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    );
    setShowAdd(false);
  };

  const handleUpdate = async (id: string, data: { name: string; color: string; notes: string }) => {
    const client = await updateClient(id, data);
    setClients((prev) =>
      prev
        .map((c) => (c.id === id ? client : c))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    );
    setEditingId(null);
  };

  const handleDelete = async (id: string) => {
    if (deletingId !== id) { setDeletingId(id); return; }
    await deleteClient(id);
    setClients((prev) => prev.filter((c) => c.id !== id));
    setDeletingId(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div style={{ padding: '32px', maxWidth: 960, margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.3px' }}>Clients</h1>
          <p style={{ color: '#64748B', fontSize: 14, marginTop: 4 }}>
            Manage your client accounts and their projects
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn btn-primary">
          + New Client
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div
          className="card fade-in"
          style={{ padding: 20, marginBottom: 28, border: '2px solid #EFF6FF' }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: '#0F172A' }}>New Client</h3>
          <ClientForm onSave={handleCreate} onCancel={() => setShowAdd(false)} />
        </div>
      )}

      {/* Client list */}
      {clients.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '60px 40px',
            background: 'white',
            borderRadius: 16,
            border: '2px dashed #E2E8F0',
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏢</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#1E293B', marginBottom: 8 }}>No clients yet</div>
          <div style={{ fontSize: 14, color: '#64748B', marginBottom: 20 }}>Add your first client to get started</div>
          <button onClick={() => setShowAdd(true)} className="btn btn-primary">+ New Client</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          {clients.map((client) => (
            <div key={client.id}>
              {/* Client Header */}
              {editingId === client.id ? (
                <div className="card fade-in" style={{ padding: 20, marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#64748B', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Edit Client
                  </div>
                  <ClientForm
                    initial={client}
                    onSave={(data) => handleUpdate(client.id, data)}
                    onCancel={() => setEditingId(null)}
                  />
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    paddingBottom: 10,
                    marginBottom: 10,
                    borderBottom: `3px solid ${client.color}`,
                  }}
                >
                  {/* Color dot */}
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: client.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 800,
                      fontSize: 14,
                      flexShrink: 0,
                    }}
                  >
                    {client.name.slice(0, 1).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 18, color: '#0F172A', letterSpacing: '-0.2px' }}>
                      {client.name}
                    </div>
                    {client.notes && (
                      <div style={{ fontSize: 13, color: '#64748B', marginTop: 2 }}>{client.notes}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      onClick={() => setEditingId(client.id)}
                      className="btn btn-secondary btn-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(client.id)}
                      className="btn btn-danger btn-sm"
                    >
                      {deletingId === client.id ? 'Confirm?' : 'Delete'}
                    </button>
                    {deletingId === client.id && (
                      <button onClick={() => setDeletingId(null)} className="btn btn-ghost btn-sm">
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Projects */}
              {client.projects && client.projects.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {client.projects.map((p) => (
                    <div
                      key={p.id}
                      className="card"
                      style={{
                        padding: '14px 18px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                      }}
                    >
                      {/* Project name */}
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#1E293B', minWidth: 200, flexShrink: 0 }}>
                        {p.name}
                      </div>
                      {/* Divider */}
                      <div style={{ width: 1, height: 20, background: '#E2E8F0', flexShrink: 0 }} />
                      {/* Project notes */}
                      <div style={{ flex: 1, fontSize: 13, color: '#64748B', lineHeight: 1.5 }}>
                        {p.notes || <span style={{ color: '#CBD5E1', fontStyle: 'italic' }}>No notes</span>}
                      </div>
                      {/* Active badge */}
                      {p.status === 'active' && (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: '#16A34A',
                            background: '#F0FDF4',
                            border: '1px solid #BBF7D0',
                            borderRadius: 20,
                            padding: '2px 10px',
                            flexShrink: 0,
                          }}
                        >
                          ● Active
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#94A3B8', padding: '10px 4px', fontStyle: 'italic' }}>
                  No projects yet
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
